import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { Pagination } from 'site-modules/shared/components/pagination/pagination';

import './common-pagination.scss';

export const CommonPagination = memo(
  ({
    pageNumber = 1,
    onPageLinkClick,
    pagesCount = 0,
    maxAllowedPageNumber = 0,
    pageSize = 1,
    pageResultsCount = 0,
    totalCount = 0,
    classes = '',
    isMobile = false,
    paginationLinkBuilder,
    numConfig = {
      mobile: {
        allAvailablePageNum: 4,
        startNumWithoutTruncate: 4,
        middleNumWithoutTruncate: 3,
        endNumWithoutTruncate: 2,
      },

      desktop: {
        allAvailablePageNum: 6,
        startNumWithoutTruncate: 6,
        middleNumWithoutTruncate: 5,
        numLeftOfCurrentMiddle: 2,
        endNumWithoutTruncate: 5,
        totalMiddleNumWithoutTruncate: 5,
      },
    },
    showNoPagesCopy = false,
    dataTrackingParent,
    linkComponent,
  }) => {
    const pagesCountNum = maxAllowedPageNumber && pagesCount > maxAllowedPageNumber ? maxAllowedPageNumber : pagesCount;
    const totalInvCountOnPrevPages = (pageNumber - 1) * pageSize;
    const firstInvOnPageNum = numberWithCommas(totalInvCountOnPrevPages + 1);
    const lastInvOnPageNum = numberWithCommas(totalInvCountOnPrevPages + pageResultsCount);

    return (
      <div
        className={classNames(
          'common-pagination row justify-content-center align-items-center flex-nowrap flex-column',
          classes,
          { desktop: !isMobile }
        )}
        data-tracking-parent={dataTrackingParent}
      >
        {pagesCount && totalCount ? (
          <>
            <div
              className={classNames(
                'common-pagination-inner d-flex flex-row align-items-center flex-nowrap justify-content-around py-0_5 py-md-0_25',
                {
                  'common-pagination-inner-mobile': isMobile,
                  clear: pagesCount === 1,
                }
              )}
            >
              <Pagination
                currentNum={pageNumber}
                totalNum={pagesCountNum}
                paginationLinkBuilder={paginationLinkBuilder}
                listClassName="d-flex justify-content-around align-items-center px-0_25 w-100"
                className="d-flex justify-content-around align-items-center"
                arrowClassName="d-flex align-items-center text-blue-30 size-16 fw-bold px-1 px-md-1_5 py-0_5 py-md-0_25"
                numLinkClassname="num-link text-blue-30"
                numLinkWrapperClassname={classNames('pagination-element pos-r', {
                  'mx-md-1_25': !isMobile,
                  'mx-1_25': isMobile,
                })}
                currentNumLinkClassname="fw-bold current-num text-white"
                currentNumLinkWrapperClassname={classNames('pagination-element num-link-current', {
                  'mx-md-1_25': !isMobile,
                  'mx-1_25': isMobile,
                })}
                dotsClassname={classNames('dots pagination-element medium text-gray-darker', {
                  'mx-md-0_25': !isMobile,
                  'mx-0_25': isMobile,
                })}
                dataTrackingId="view_search_result"
                arrowDataTrackingId="view_search_result"
                arrowsDataTrackingValues={{
                  prev: 'previous',
                  next: 'next',
                }}
                onPageLinkClick={onPageLinkClick}
                onArrowLinkClick={onPageLinkClick}
                numConfig={numConfig[isMobile ? 'mobile' : 'desktop']}
                arrowsText={isMobile ? undefined : { right: 'Next', left: 'Previous' }}
                showDisabledArrows={pagesCountNum > 1}
                showLastNum={false}
                isTextForMobile={false}
                linkComponent={linkComponent}
                isSrp
              />
            </div>
            <div className="mt-1 small">
              <span className="text-nowrap text-cool-gray-40">
                Showing {firstInvOnPageNum} - {lastInvOnPageNum} out of {numberWithCommas(totalCount)} listings
              </span>
            </div>
          </>
        ) : (
          <>{showNoPagesCopy && <div className="display-6 mt-0_5">There are no pages to show</div>}</>
        )}
      </div>
    );
  }
);

CommonPagination.propTypes = {
  pageNumber: PropTypes.number,
  pagesCount: PropTypes.number,
  maxAllowedPageNumber: PropTypes.number,
  pageResultsCount: PropTypes.number,
  pageSize: PropTypes.number,
  totalCount: PropTypes.number,
  classes: PropTypes.string,
  onPageLinkClick: PropTypes.func,
  isMobile: PropTypes.bool,
  paginationLinkBuilder: PropTypes.func,
  numConfig: PropTypes.shape({
    mobile: PropTypes.shape({}),
    desktop: PropTypes.shape({}),
  }),
  showNoPagesCopy: PropTypes.bool,
  dataTrackingParent: PropTypes.string,
  linkComponent: PropTypes.elementType,
};
